"use client";

import React from "react";
import { Rating, Stack, Typography } from "@mui/material";
import { Box } from "@mui/system";
import Image from "next/image";
import { Tour } from "@/types/dto/tours/tour";
import { ColletteButton, ColletteChip } from "@collette-travel/collette-common-controls";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { tourCardBadgeFactory } from "@/factories/search/tour-search-response-factory";

const CARD_WIDTH = 345;
const MOBILE_CARD_WIDTH = 300;
interface TourCardProps {
  tour: Tour;
  carouselPosition?: number;
  showRating?: boolean;
  openRequestAQuote?: (tour: Tour) => void;
}

const TourCard: React.FC<TourCardProps> = ({
  tour,
  carouselPosition,
  showRating,
  openRequestAQuote
}) => {
  const t = useTranslations("DESTINATIONS");
  const reviewsText = useTranslations("PDP")("Reviews");

  // Extract numeric price from formatted string (e.g., "$3,999" -> "3999")
  const numericPrice = tour.price.replace(/[^0-9]/g, "");

  // Process badges using the factory
  const badgeInput = tour.badges || (tour.badge ? [tour.badge] : null);
  const badges = tourCardBadgeFactory(badgeInput);

  return (
    <Box
      component="section"
      data-testid="tour-card"
      className="single-tour-card"
      data-tour-name={tour.title}
      data-tour-price={numericPrice}
      data-tour-duration={String(tour.days)}
      // data-tour-activity-level="pending-api" // TODO(WR-485): Replace when API provides activityLevel
      // data-tour-style="pending-api" // TODO(WR-485): Replace when API provides tourStyles
      data-tour-id={(tour as any).neoId}
      data-ranking-algorithm="algorithmic"
      data-carousel-position={carouselPosition ? String(carouselPosition) : undefined}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0,
        alignItems: "flex-start",
        justifyContent: "flex-start",
        flexShrink: 0,
        width: { lg: CARD_WIDTH, md: CARD_WIDTH, sm: MOBILE_CARD_WIDTH, xs: MOBILE_CARD_WIDTH },
        position: "relative",
        boxShadow: 6,
        borderRadius: "20px",
        backgroundColor: "background.default",
        overflow: "visible",
        cursor: "pointer"
      }}
    >
      {/* Image */}
      <Box
        sx={{
          width: "100%",
          height: "252px",
          position: "relative",
          flexShrink: 0
        }}
      >
        <Image
          src={tour.searchImage.imageUrl || ""}
          alt={tour.searchImage.altText || ""}
          fill
          style={{
            objectFit: "cover",
            borderRadius: "20px 20px 0 0",
            background: "var(--theme-palette-background-warm_strong)"
          }}
          data-testid="tour-card-image"
        />
        {/* Badges */}
        {badges.length > 0 && (
          <Stack
            sx={{
              position: "absolute",
              top: "1.6rem",
              right: "1.6rem",
              gap: "1rem",
              flexDirection: "row",
              flexWrap: "wrap",
              justifyContent: "flex-end"
            }}
            data-testid="tour-card-badges"
          >
            {badges.map((badge) => (
              <ColletteChip
                key={badge.label}
                label={badge.label}
                color={badge.color}
                sx={{
                  borderRadius: "0.8rem",
                  fontWeight: 600,
                  fontSize: "1.2rem"
                }}
                testId={`tour-card-badge-${badge.label}`}
              />
            ))}
          </Stack>
        )}
      </Box>

      {/* Card Content */}
      <Box
        className="content-container"
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: { xs: "12px", md: "12px" }, // gap stays the same
          alignSelf: "stretch",
          p: { xs: "20px", md: "20px" }, // padding stays the same
          borderRadius: { xs: "0 0 20px 20px", md: "0 0 20px 20px" }, // border radius stays the same
          border: "1px solid",
          borderColor: "divider", // border color for mobile and desktop
          borderTop: "none",
          backgroundColor: "background.default", // background color for mobile and desktop
          flexShrink: 0,
          position: "relative"
        }}
        data-testid="tour-card-content"
      >
        {/* Rating Section - Reserved space for consistent height */}
        {showRating && (
          <Box sx={{ height: "24px", alignSelf: "stretch" }}>
            {tour.rating !== undefined && tour.rating > 0 && (
              <Stack direction="row" alignItems="center" spacing="12px">
                <Rating
                  defaultValue={tour.rating}
                  precision={0.1}
                  size="small"
                  readOnly
                  data-testid="feefo-review_star-rating"
                />
                <Typography
                  data-testid="tour-card-destination"
                  component="h4"
                  sx={{
                    color: "text.primary",
                    fontSize: "12px",
                    fontWeight: 500
                  }}
                >
                  ({tour.reviewCount} {reviewsText})
                </Typography>
              </Stack>
            )}
          </Box>
        )}
        {/* Title */}
        <Typography
          data-testid={`tour-card-title_${carouselPosition}`}
          component="h3"
          sx={{
            color: "text.primary",
            textAlign: "left",
            fontSize: "18px",
            lineHeight: "150%",
            fontWeight: 700,
            position: "relative",
            alignSelf: "stretch",
            height: "3lh",
            mb: 0,
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "normal"
          }}
        >
          {tour.title}
        </Typography>

        {/* Offer Section */}
        <Box
          className="offer-section"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            alignItems: "flex-start",
            justifyContent: "flex-start",
            alignSelf: "stretch",
            flexShrink: 0,
            position: "relative"
          }}
        >
          {/* Days */}
          <Box
            className="_13-days"
            data-testid={`tour-card-days_${carouselPosition}`}
            sx={{
              color: "text.primary",
              textAlign: "left",
              fontSize: "var(--desktop-subtitle-1-font-size, 18px)",
              lineHeight: "var(--desktop-subtitle-1-line-height, 150%)",
              fontWeight: "var(--desktop-subtitle-1-font-weight, 500)",
              alignSelf: "stretch"
            }}
          >
            {tour.days} {t("days")}
          </Box>

          {/* Pricing */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "8px",
              alignSelf: "stretch"
            }}
          >
            <Typography
            data-testid={`tour-card-price-label-${carouselPosition}`}
              sx={{
                color: "text.primary",
                fontSize: "18px",
                fontStyle: "normal",
                fontWeight: 500,
                lineHeight: "150%",
                whiteSpace: "nowrap"
              }}
            >
              From
            </Typography>

            <Typography
            data-testid={`tour-card-price-${carouselPosition}`}
              sx={{
                color: "secondary.main",
                fontSize: "18px",
                fontStyle: "normal",
                fontWeight: 700,
                lineHeight: "150%",
                whiteSpace: "nowrap"
              }}
            >
              {tour.price}*
            </Typography>

            {tour.strikeOutPrice && (
              <Typography
                data-testid={`tour-card-strikeout-price-${carouselPosition}`}
                sx={{
                  color: "text.disabled",
                  fontSize: "18px",
                  fontStyle: "normal",
                  fontWeight: 500,
                  lineHeight: "150%",
                  textDecoration: "line-through",
                  whiteSpace: "nowrap"
                }}
              >
                {tour.strikeOutPrice}*
              </Typography>
            )}
          </Box>

          {/* Offer Box */}
          {tour.offerSavings && tour.offerSavings !== "" && (
            <Box
              className="offer-box"
              data-testid={`tour-card-offer-text-${carouselPosition}`}
              sx={{
                backgroundColor: "accent.gold",
                borderRadius: "10px",
                px: "10px",
                py: "6px",
                display: "flex",
                flexDirection: "row",
                gap: "10px",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                position: "relative",
                mt: 1,
                mb: "1px"
              }}
            >
              <span
                className="save-up-to-350"
                data-testid={`tour-card-offer-savings-${carouselPosition}`}
                style={{
                  color: "var(--theme-palette-text-primary)",
                  textAlign: "right",
                  fontSize: 14,
                  lineHeight: "150%",
                  letterSpacing: "0.4px",
                  fontWeight: 600,
                  position: "relative"
                }}
              >
                {t("Save up to")} {tour.offerSavings}
              </span>
            </Box>
          )}
        </Box>

        {/* Button */}
        <Box
          className="button-container"
          sx={{
            display: "flex",
            flexDirection: openRequestAQuote ? "row" : "column",
            justifyContent: "flex-start",
            gap: "10px",
            alignItems: "flex-end",
            alignSelf: "stretch",
            flexShrink: 0,
            position: "relative",
            mt: tour.offerSavings ? 2 : "7rem" // Add extra margin when offer box is hidden
          }}
        >
          {openRequestAQuote && (
            <ColletteButton
              variant="outlined"
              size="large"
              label={t("Request a Quote")}
              testId={`request-quote-${carouselPosition}`}
              data-track="click"
              data-interaction-element="button"
              onClick={() => openRequestAQuote(tour)}
              sx={{
                textWrap: "nowrap",
                zIndex: 10,
                position: "relative"
              }}
            />
          )}
          <ColletteButton
            label={t("View Tour")}
            href={tour.tourUrl}
            testId={`tour-card-button-${carouselPosition}`}
            data-track="click"
            data-interaction-element="button"
            color="primary"
            size="large"
            sx={{
              textWrap: "nowrap",
              zIndex: 10,
              position: "relative"
            }}
          />
        </Box>
      </Box>

      {/* Full card clickable link */}
      <Link
        href={tour.tourUrl}
        tabIndex={-1}
        style={{ zIndex: 5 }}
        className="pseudo-absolute-full-container"
        data-testid="tour-card-full-link"
        data-track="click"
        data-interaction-element="card"
      />
    </Box>
  );
};
export default TourCard;
